import React, { useState, useEffect, useRef, useId, useMemo } from 'react';
import ReactApexChart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Box, Typography } from '@mui/material';
import { FONT_COLOR } from '../const/general'
import { CHRONOLOGY_LEVEL_COLOR, CHRONOLOGY_LEVEL_LABEL } from '../const/color';
import Progress from '../item/progress'

export default function BarStack(props) {
    const [sliderValue, setSliderValue] = useState([0, props.distance]);
    const [categories, setCategories] = useState([]);
    const [allData, setAllData] = useState([]);
    const [riskData, setRiskData] = useState([]);
    const [seriesMax, setSeriesMax] = useState(props.limitVal + 1);
    const [isNoData, setIsNoData] = useState(false);
    const [areaNameAry, setAreaNameAry] = useState([]);
    const [areaRiverNameAry, setAreaRiverNameAry] = useState([]);
    const [tooltipInfo, setTooltipInfo] = useState(null);

    // stacked 判定と色配列
    const [stackedMode, setStackedMode] = useState(false);
    const [stackedColors, setStackedColors] = useState([]);

    // チャートごとの一意のキー
    const chartId = useId();
    // 監視用Ref
    const sliderValueRef = useRef(sliderValue);

    const getLabelValue = (val) => {
        if (val === '-') return '-';
        const num = Number(val);
        if (!isNaN(num)) return String(Math.round(num)); // 小数点なしにする
        return '';
    };

    const getYValue = (val) => {
        if (val === '-') return 0;
        const num = Number(val);
        return !isNaN(num) ? num : 0;
    };

    // 被害キー順／ラベル／色（必要なら順序を調整）
    const DAMAGE_KEYS = [
        ['int_river_burst_num', CHRONOLOGY_LEVEL_LABEL.LEVER_8, CHRONOLOGY_LEVEL_COLOR.LEVER_8],
        ['int_other_num', CHRONOLOGY_LEVEL_LABEL.LEVER_9, CHRONOLOGY_LEVEL_COLOR.LEVER_9],
        ['int_regulation_num', CHRONOLOGY_LEVEL_LABEL.LEVER_7, CHRONOLOGY_LEVEL_COLOR.LEVER_7],
        ['int_power_outage_num', CHRONOLOGY_LEVEL_LABEL.LEVER_5, CHRONOLOGY_LEVEL_COLOR.LEVER_5],
        ['int_water_outage_num', CHRONOLOGY_LEVEL_LABEL.LEVER_6, CHRONOLOGY_LEVEL_COLOR.LEVER_6],
        ['int_collapse_num', CHRONOLOGY_LEVEL_LABEL.LEVER_1, CHRONOLOGY_LEVEL_COLOR.LEVER_1],
        ['int_fallen_tree_num', CHRONOLOGY_LEVEL_LABEL.LEVER_4, CHRONOLOGY_LEVEL_COLOR.LEVER_4],
        ['int_flood_num', CHRONOLOGY_LEVEL_LABEL.LEVER_3, CHRONOLOGY_LEVEL_COLOR.LEVER_3],
        ['int_landslide_num', CHRONOLOGY_LEVEL_LABEL.LEVER_2, CHRONOLOGY_LEVEL_COLOR.LEVER_2],
    ];

    // チャート情報を格納
    const setChartData = (data) => {
        const isOver = props.distance < data.length;
        const { rankData, series_ary, category_ary, tempAreaNameAry, tempAreaRiverNameAry } = props.getRankData(data, isOver, sliderValueRef, getLabelValue, getYValue);

        // rankData は市町村オブジェクト配列（例: { city_code, city_name, total, int_* }）
        // 被害種別フィールドがあるか判定（小文字・大文字両対応）
        const sample = rankData && rankData.length > 0 ? rankData[0] : null;
        const hasDamageFields = sample && DAMAGE_KEYS.some(k => (k[0] in sample) || (k[0].toUpperCase() in sample));

        if (hasDamageFields) {
            // 積み上げシリーズを作る
            const cats = rankData.map(item => item.date || '');
            const builtSeries = [];
            const builtColors = [];

            DAMAGE_KEYS.forEach(([key, label, color]) => {
                const keyLower = key;
                const keyUpper = key.toUpperCase();
                const seriesData = rankData.map(item => {
                    const rawVal = (item[keyLower] !== undefined) ? item[keyLower] : (item[keyUpper] !== undefined ? item[keyUpper] : 0);
                    const y = Number(rawVal) || 0;
                    return {
                        x: '',
                        y,
                        custom: {
                            city_name: item.city_name || item.name || '',
                            city_code: item.city_code || item.code || null,
                            total: item.total || (Number(item.INT_ALL_NUM || item.int_all_num) || 0),
                            codes: item.codes || [],
                            msh_code: item.msh_code || []
                        }
                    };
                });
                const totalForSeries = seriesData.reduce((s, p) => s + (Number(p.y) || 0), 0);
                if (totalForSeries > 0) {
                    builtSeries.push({
                        name: label,
                        data: seriesData
                    });
                    builtColors.push(color);
                }
            });

            // categories と series を上書き
            setAllData(rankData);
            setRiskData(builtSeries);
            setCategories(cats);
            setAreaNameAry(tempAreaNameAry);
            setAreaRiverNameAry(tempAreaRiverNameAry);

            // stacked フラグ / 色をセット
            setStackedMode(true);
            setStackedColors(builtColors);

            // seriesMax を更新（各カテゴリの合計最大を用いる）
            const sums = rankData.map(item => {
                return DAMAGE_KEYS.reduce((acc, [key]) => {
                    const v = (item[key] !== undefined) ? Number(item[key]) : (item[key.toUpperCase()] !== undefined ? Number(item[key.toUpperCase()]) : 0);
                    return acc + (isNaN(v) ? 0 : v);
                }, 0);
            });
            const maxSum = sums.length > 0 ? Math.max(...sums) : props.limitVal;
            setSeriesMax(Math.max(props.limitVal + props.plusValue, maxSum + props.plusValue));
            return;
        }

        // 被害フィールドがない場合は既存ロジック（単一 series）に従う
        setAllData(rankData);
        setRiskData(series_ary);
        setCategories(category_ary);
        setAreaNameAry(tempAreaNameAry);
        setAreaRiverNameAry(tempAreaRiverNameAry);
        setStackedMode(false);
        setStackedColors([]);

        setSeriesMax(props.limitVal + props.plusValue)
        if (rankData.length > 0) {
            // 既存の最大値の取得
            const max = Math.max(...rankData.map(item => item.max || item.total || 0));
            if (max > props.limitVal) {
                setSeriesMax(max + props.plusValue)
            }
        }
    }

    // ローディング終了
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    }

    // series の形状は stackedMode によって変わる
    const series = useMemo(() => {
        if (stackedMode) {
            return riskData; // すでに複数 series 配列
        }
        return [
            {
                name: '',
                data: riskData
            },
        ];
    }, [riskData, stackedMode]);

    // データ更新が完了したらローディング終了
    useEffect(() => {
        if (allData !== null) {
            const timer = setTimeout(() => {
                loadingEnd();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [allData]);

    // 定期監視用
    useEffect(() => {
        if (props.allRankData === null ||
            props.allRankData === undefined ||
            props.allRankData.length === 0
        ) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.allRankData && props.allRankData.length > 0) {
            setChartData(props.allRankData)
        }
        props?.setSelectedRoadName?.(null);
        setIsNoData(false)

        // 依存関係の「dateArray」は削除しないでください。
        // これがないとprops.allRankDataに変化がない場合にローディングが消えなくなるので
    }, [props.dateArray, props.allRankData]);

    useEffect(() => {
        if (props.allRankData === null ||
            props.allRankData === undefined ||
            props.allRankData.length === 0
        ) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.allRankData && props.allRankData.length > 0) {
            setChartData(props.allRankData)
        }
        props?.setSelectedRoadName?.(null);
        setIsNoData(false)

        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);

    // スライダーが移動された場合
    useEffect(() => {
        sliderValueRef.current = sliderValue;
        setChartData(allData)
    }, [sliderValue]);

    const options = useMemo(() => ({
        chart: {
            id: chartId,
            type: "bar",
            toolbar: {
                show: false,
                offsetY: 0
            },
            stacked: stackedMode,
            events: {
                /**
                 * バーをクリックした時のイベント処理
                 * @param {Object} event イベントオブジェクト
                 * @param {Object} chartContext チャートのコンテキスト
                 * @param {Object} config 各種設定値（dataPointIndex などが含まれる）
                 */
                dataPointSelection: (event, chartContext, config) => {
                    const { dataPointIndex, w } = config;
                    // 1. 全てのシリーズ（積み上げられた全データ）を走査し、
                    //    クリックされた時間帯（dataPointIndex）のデータを抽出
                    const allCodesInTimeStep = w.config.series
                        .map(s => s.data[dataPointIndex]?.custom?.msh_code) // codes配列を取得
                        .filter(msh_code => Array.isArray(msh_code))           // 配列が存在するもののみ抽出
                        .flat();                                         // 二次元配列を一次元に平坦化

                    // 2. 重複を削除（同じ時間が複数のシリーズで構成されているため）
                    const uniqueCodes = [...new Set(allCodesInTimeStep)];
                    const timeTms = w.config.xaxis.categories[dataPointIndex];

                    // 3. 親コンポーネントに通知
                    if (props.setSelectMsh && uniqueCodes.length > 0) {
                        props.setSelectMsh(uniqueCodes);
                    }

                    if (props.setSelectTimeTmsChronology && timeTms != null) {
                        props.setSelectTimeTmsChronology(timeTms);
                    }
                },
                dataPointMouseEnter: (event, chartContext, config) => {
                    if (props?.dataPointMouseEnter) {
                        setTooltipInfo(props.dataPointMouseEnter(event, config, series));
                    }
                },
                mouseLeave: () => {
                    setTooltipInfo(null);
                }
            }
        },
        annotations: {
            points: allData.map((item, index) => {
                let actualTotal = 0;
                if (stackedMode) {
                    actualTotal = DAMAGE_KEYS.reduce((sum, [key]) => {
                        const keyLower = key;
                        const keyUpper = key.toUpperCase();
                        const val = (item[keyLower] !== undefined) ? item[keyLower] : (item[keyUpper] !== undefined ? item[keyUpper] : 0);
                        return sum + (Number(val) || 0);
                    }, 0);
                } else {
                    actualTotal = (item.total !== undefined && item.total !== null)
                        ? item.total
                        : (Number(item.INT_ALL_NUM || item.int_all_num) || 0);
                }

                return {
                    x: item.date || '',
                    y: actualTotal,
                    marker: {
                        size: 0
                    },
                    label: {
                        text: `${actualTotal}`,
                        borderWidth: 0,
                        offsetX: 0,
                        offsetY: 0,
                        textAnchor: 'middle',
                        style: {
                            color: FONT_COLOR,
                            fontSize: '12px',
                            fontWeight: 600,
                            background: 'transparent'
                        }
                    }
                };
            })
        },
        legend: { show: false },
        colors: [
            function ({ value, seriesIndex, dataPointIndex, w }) {
                if (props.getRankColor) {
                    return props.getRankColor(value, series, seriesIndex, dataPointIndex, w)
                }
            }
        ],
        plotOptions: {
            bar: {
                horizontal: false,
                stacked: stackedMode,
                dataLabels: {
                    position: 'center' // 縦方向中央に配置
                }
            }
        },
        grid: {
            show: false,
            padding: {
                left: 45,
                right: 12,
                top: -10,
                bottom: -13
            }
        },
        dataLabels: {
            enabled: true,
            offsetX: 0,
            offsetY: 0,
            style: {
                fontSize: props.fontSize,
                colors: [
                    function ({ seriesIndex, dataPointIndex, w }) {
                        const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
                        // 水平バーの場合、dataPointは { x: '', y: value, custom: {...} } の形式
                        const actualValue = dataPoint.y;
                        if (props.getRankLabelColor) {
                            return props.getRankLabelColor(actualValue, series, seriesIndex, dataPointIndex, w);
                        }
                        // デフォルトの色を返す
                        return FONT_COLOR;
                    }
                ]
            },
            formatter: function (val, opts) {
                return props.getFormat ? props.getFormat(val, opts) : val.toString();
            }
        },
        xaxis: {
            categories: categories,
            labels: {
                show: true,
                rotate: -55,
                rotateAlways: true,
                style: {
                    colors: FONT_COLOR,
                    fontSize: '12px'
                }
            }
        },
        yaxis: {
            show: false,
            axisTicks: {
                show: false,
            },
            axisBorder: {
                show: false
            },
            labels: {
                show: false
            },
            min: 0,
            max: seriesMax
        },
        tooltip: {
            enabled: props.enabled,
            x: {
                formatter: function (val, opts) {
                    return props.tooltipFormatX(opts);
                }
            },
            y: {
                formatter: function (val, opts) {
                    return props.tooltipFormatY(val);
                }
            },
            custom: function (opts) {
                return props.tooltipFormatCustom(opts, areaNameAry, areaRiverNameAry);
            }
        }
    }), [categories, seriesMax, stackedMode, JSON.stringify(stackedColors)]);

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
        if (series && series.length > 0) {
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [series]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    height: '100%',
                    overflow: 'hidden' // スクロール非表示
                }}
            >
                {/* チャート部分 */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {!props.isReadyFlg ?
                        <Progress />
                        :
                        isNoData ?
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    color: 'white'
                                }}
                            >
                                対象データがありません
                            </Typography>
                            :
                            <Box sx={{ display: 'flex', position: 'relative', flex: 1, overflow: 'hidden', height: "100%" }}>
                                {/* チャート本体 */}
                                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                    <ReactApexChart
                                        options={options}
                                        series={series}
                                        type="bar"
                                        height="100%"
                                        width="100%"
                                        style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                                    />
                                    {tooltipInfo?.data && (
                                        props.tooltipContent(tooltipInfo)
                                    )}
                                </Box>
                            </Box>
                    }
                </Box>
            </Box>
        </>
    );
}